import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Box } from '@abyss/web/ui/Box';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import { find } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';

import { isUmrMember } from '../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../context/PortalContext';
import { useLagoon } from '../../hooks/useLagoon';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { cardHeaderMyBenefits, phoneOnly } from '../ConstantsStyles';
import { ContentWrapper } from '../ContentWrapper';
import { IconBrandAndSymbol } from '../IconBrandAndSymbol';
import { IconName } from '../IconBrandAndSymbol/types';
import {
  BoxStyles,
  ChipContainer,
  ContainerStyle,
  textContent,
} from './StillNeedHelp.style';

export const StillNeedHelp = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const largeScreen = useMediaQuery('(max-width: 1100px)');

  const [chatAvailable, setChatAvailable] = useState(false);

  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const stillNeedHelpTitle = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.STILL_NEED_HELP_TITLE,
  });

  const alignEmbeddedChatContent = () => {
    const row = document.querySelector(
      '#PSX-LPMContainer [data-lp-event="click"] a > div:first-of-type'
    ) as HTMLElement | null;
    if (!row) return false;
    row.style.display = 'flex';
    row.style.alignItems = 'center';

    return true;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const lpmContainerSelector = '[id^="LPMcontainer-"]';
      if (document.querySelector(lpmContainerSelector) != null) {
        alignEmbeddedChatContent();
        setChatAvailable(true);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const list = [
    {
      title:
        stillNeedHelpTitle?.message ??
        (isUmr
          ? t('STILL_NEED_HELP.TITLE_UMR')
          : t('STILL_NEED_HELP.TITLE_PHONE')),
      content1: t('STILL_NEED_HELP.CONTENT_PHONE1'),
      content2: t('STILL_NEED_HELP.CONTENT_PHONE2'),
      icon: 'phone',
      href: `tel:18778444999`,
      adobeLikType: 'phone',
      dataTestId: 'call-us-card',
      display: true,
      divId: 'call-us',
      key: '1',
    },
  ];

  const handleClick = (dataTestId, adobeLinkType) => {
    adobeLinkTrackEvent({
      name: dataTestId,
      location: 'body:still need help',
      type: adobeLinkType,
    });
  };

  return (
    <React.Fragment>
      <Box color="$gray1" css={BoxStyles}>
        <ContentWrapper>
          <Heading
            css={cardHeaderMyBenefits}
            data-auto-testid="still-need-help-heading"
            data-testid="still-need-help-heading"
            display="h4"
            offset={1}
          >
            {t('STILL_NEED_HELP.HEADING')}
          </Heading>
          <ChipContainer>
            <div id={Constants.LIVE_PERSON_EMBEDDED_CHAT_DIV_ID} />
            {!chatAvailable &&
              list.map(
                (item) =>
                  item.display && (
                    <ContainerStyle
                      data-auto-testid={`${item.dataTestId}`}
                      data-testid={`${item.dataTestId}`}
                      href={!isUmr ? item.href : undefined}
                      id={item.divId}
                      key={item.divId}
                      onClick={
                        !isUmr
                          ? () =>
                              handleClick(item.dataTestId, item.adobeLikType)
                          : undefined
                      }
                    >
                      {' '}
                      <Flex
                        alignItems="center"
                        css={{ gap: '10px', width: '85%' }}
                      >
                        <IconBrandAndSymbol
                          icon={IconName.phone}
                          size={48}
                          variant="twotone"
                        />
                        <Layout.Stack alignItems="left" css={textContent}>
                          <Text
                            fontWeight={700}
                            size={mobileScreen ? '$md' : '$lg'}
                          >
                            {item.title}
                          </Text>
                          <Flex direction={largeScreen ? 'column' : 'row'}>
                            <Text
                              fontWeight={500}
                              size={mobileScreen ? '$sm' : '$md'}
                            >
                              {item.content1}
                            </Text>
                            <Text
                              fontWeight={500}
                              size={mobileScreen ? '$sm' : '$md'}
                            >
                              &nbsp;{item.content2}
                            </Text>
                          </Flex>
                        </Layout.Stack>
                      </Flex>
                      {!isUmr && (
                        <Button rounded size="$sm" variant="ghost">
                          <IconMaterial icon="chevron_right" />
                        </Button>
                      )}
                    </ContainerStyle>
                  )
              )}
          </ChipContainer>
        </ContentWrapper>
      </Box>
    </React.Fragment>
  );
};
